import { useQuery } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account } from '@xinfer/api-client';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DescriptionItem,
  DescriptionList,
  ErrorState,
  PageHeader,
  SectionHeader,
  Skeleton,
  usePageTitle,
} from '@xinfer/ui';
import { CreditWindowBar } from '../components/CreditWindowBar';
import { explainCredits, formatCredits, formatTimestamp } from '../lib/format';

/**
 * The plan, in the unit it is actually sold in.
 *
 * Three windows and one number each. The rate card is deliberately small
 * enough to state in full on one page — no request counts, no per-model
 * multipliers — and the point of publishing it that way is lost if the portal
 * only shows a total and asks people to trust it. So the weights are shown,
 * and the arithmetic is shown, and every figure on the page can be checked by
 * hand.
 */
export function Credits({ account }: { account: Account }) {
  usePageTitle('Plan & credits');

  const credits = useQuery({
    queryKey: ['credits', account.id],
    queryFn: () => client.credits(account.id),
    // A limit, not a report: someone watching a window they are about to hit
    // wants it to move without a reload.
    refetchInterval: 60_000,
  });

  const standing = credits.data;
  const capped = standing?.windows.some((w) => w.cap_milli !== null) ?? false;

  return (
    <>
      <PageHeader
        title="Plan & credits"
        description="What this plan includes, what has been used, and how much is left in each window."
        meta={<Badge variant="outline">{standing?.plan ?? account.plan}</Badge>}
      />

      {credits.isError && (
        <ErrorState
          title="Could not load credits"
          description="These figures are read from the ledger behind the api, not from the serving path — inference is unaffected, and the gate keeps enforcing the same limits either way."
        />
      )}

      {!credits.isError && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {credits.isLoading || !standing
              ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-[168px] w-full" />)
              : standing.windows.map((window) => (
                  <CreditWindowBar key={window.window} usage={window} />
                ))}
          </div>

          {standing && !capped && (
            <p className="mt-4 text-sm text-muted-foreground">
              This plan has no credit limits. Usage is still metered — the figures above are real
              — but no window will refuse a request.
            </p>
          )}

          {standing && (
            <p className="mt-4 text-sm text-muted-foreground">
              Measured at {formatTimestamp(standing.as_of)}. The gate adds whatever it has served
              since, so a limit takes effect immediately rather than at the next refresh.
            </p>
          )}

          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>What a credit is</CardTitle>
                <CardDescription>
                  One credit is 1,000 weighted tokens. There is nothing else in the rate card — no
                  request counts, no per-model multipliers.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs text-foreground">
                  credits = (cached x {weight(standing?.weights.micro_per_cached_input_token, 20)} +
                  uncached x {weight(standing?.weights.micro_per_uncached_input_token, 1000)} +
                  output x {weight(standing?.weights.micro_per_output_token, 2000)}) / 1000
                </pre>
                <p>
                  A prompt token served from the prefix cache costs a fiftieth of one that has to
                  be computed, because a cache hit skips prefill entirely. That is the single
                  biggest lever on this page: an agent that re-sends a stable context every turn
                  pays almost nothing for it.
                </p>
                <p>
                  Cached prompt tokens are a <em>subset</em> of prompt tokens, never an addition to
                  them. Adding the two together double-counts the cached half of every prompt.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Why three windows</CardTitle>
                <CardDescription>
                  Each one protects something different, and only one of them is the number worth
                  comparing against a competitor.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DescriptionList>
                  <DescriptionItem label="5 hours">
                    The burst allowance. It is what bounds a single intense session, and it is the
                    window that will stop you mid-flow — so it is the one to watch. It rolls
                    continuously, not at a fixed hour.
                  </DescriptionItem>
                  <DescriptionItem label="7 days">
                    The anti-drain guard, set about 21% above an even weekly pace. It exists so
                    that nobody burns a month's allowance in a week and then finds themselves cut
                    off for three.
                  </DescriptionItem>
                  <DescriptionItem label="30 days">
                    The headline allowance. The other two are what make it safe to offer.
                  </DescriptionItem>
                </DescriptionList>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8">
            <SectionHeader
              title="Balance"
              description="Credits granted and not yet drawn: the plan's allowance plus any top-up packs."
            />
            <Card>
              <CardContent className="pt-6">
                <DescriptionList>
                  <DescriptionItem label="Available">
                    {standing ? (
                      <>
                        <span className="font-medium text-foreground">
                          {formatCredits(standing.grant_balance_milli)} credits
                        </span>{' '}
                        — {explainCredits(standing.grant_balance_milli)}.
                      </>
                    ) : (
                      '—'
                    )}
                  </DescriptionItem>
                  <DescriptionItem label="Expiry">
                    Plan credits do not expire at the end of the month. Top-up packs are valid for
                    365 days from purchase, and the pack closest to expiring is always spent first.
                  </DescriptionItem>
                  <DescriptionItem label="Past the allowance">
                    Usage beyond the balance is billed as overage on the monthly invoice rather
                    than refused — the window caps above are what actually stop a request.
                  </DescriptionItem>
                </DescriptionList>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </>
  );
}

/**
 * A weight as it appears in the formula.
 *
 * The API carries micro-credits per token; the published formula is written
 * per token in credits. Dividing by 1,000 here rather than showing the raw
 * integer keeps the line on this page identical to the one in the rate card,
 * which is the whole point of showing it.
 *
 * Falls back to the published weight while loading, so the formula does not
 * flash a row of zeros — a rate card that momentarily reads "free" is worse
 * than one that appears a beat late.
 */
function weight(micro: number | undefined, fallback: number): string {
  const value = (micro ?? fallback) / 1000;
  return value < 1 ? value.toFixed(2) : String(value);
}
